import Big from "big.js";

import { getSum, wrapDecimalOperand } from "../../../Helpers/AutocalcCommonFunctions";
import { DataDeclarationHelper } from "../DataDeclaration/DataDeclarationHelper";
import { ModelEvent, patchModelEventInfo } from "../EventManagement/ModelEvents/ModelEvent";
import { MaskResolver } from "../PathUtils/MaskResolver";
import { PathUtils } from "../PathUtils/PathUtils";
import { IFormReadonlyModelAccessor } from "../../Form/IFormModelAccessor";

export interface ValuesResult {
    paths: string[];
    values: string[];
}

export type ArgMappingType = "sum" | "count";

export type OperandGettingResult = number | "" | Big | ValuesResult;

export interface IOperandsGetter {
    get(relativeOperandNodePath: string, valueName?: string, type?: ArgMappingType): OperandGettingResult;
}

export class OperandsGetter implements IOperandsGetter {
    private readonly model: IFormReadonlyModelAccessor;
    private readonly declHelper: DataDeclarationHelper;
    private readonly basePath: string;
    private readonly eventArgs?: ModelEvent;

    public constructor(
        model: IFormReadonlyModelAccessor,
        dataDeclarationHelper: DataDeclarationHelper,
        basePath: string,
        eventArgs?: ModelEvent
    ) {
        this.basePath = basePath;
        this.eventArgs = eventArgs;
        this.model = model;
        this.declHelper = dataDeclarationHelper;
    }

    public readonly get = (
        relativeOperandNodePath: string,
        valueName?: string,
        type?: ArgMappingType
    ): OperandGettingResult => {
        const operandPath = PathUtils.getAbsolutePath(this.basePath, relativeOperandNodePath);

        if (operandPath.lastIndexOf("*") === -1) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-return
            return valueName == undefined
                ? // @ts-ignore
                  this.model.getViewModel(operandPath)
                : this.model.getViewModelField(operandPath, valueName);
        }
        if (valueName == undefined) {
            throw new Error(`For multiple elements valueName must be specified`);
        }

        if (type === "sum") {
            const path = PathUtils.join(PathUtils.splitMask(operandPath));
            const value = this.model.getViewModelField(path, type);
            if (value !== undefined && value !== "") {
                if (
                    this.eventArgs &&
                    this.declHelper.getMask(this.eventArgs.path) === this.declHelper.getMask(operandPath)
                ) {
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    return this.calculateSumIncremental(value, path);
                }
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                return wrapDecimalOperand(value);
            }
            const { values } = this.getValues(operandPath, valueName);
            return getSum(values);
        } else if (type === "count") {
            const path = PathUtils.join(PathUtils.splitMask(operandPath));

            const value = this.model.getViewModelField(path, type);
            if (
                value !== undefined &&
                this.eventArgs &&
                this.declHelper.getMask(this.eventArgs.path) === this.declHelper.getMask(operandPath)
            ) {
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                return this.calculateCountIncremental(value);
            }

            const { values } = this.getValues(operandPath, valueName);
            return this.getCount(values);
        }

        return this.getValues(operandPath, valueName);
    };

    private getValues(operandPath: string, valueName: string): ValuesResult {
        const nodePaths = MaskResolver.resolveMask(this.model, operandPath);
        const result: ValuesResult = {
            paths: [],
            values: [],
        };

        for (const path of nodePaths) {
            result.paths.push(PathUtils.getValuePath(path, valueName));
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            result.values.push(this.model.getViewModelField(path, valueName));
        }

        return result;
    }

    private getCount<T>(values: T[]): number {
        return values.filter(val => val).length;
    }

    private calculateCountIncremental(count: number): number {
        if (this.eventArgs == undefined) {
            throw new Error("EventArgs must be not null");
        }
        const { oldValue, value } = this.eventArgs;
        let newCount = Number(count);

        if (oldValue && !value) {
            newCount -= 1;
        }
        if (!oldValue && value) {
            newCount += 1;
        }

        return newCount;
    }

    private calculateSumIncremental(sumValue: number, path: string): "" | Big {
        const countValue = this.model.getViewModelField(path, "count");
        if (!countValue) {
            return "";
        }
        if (this.eventArgs == undefined) {
            throw new Error("EventArgs must be not null");
        }
        if (this.eventArgs.processedBySum) {
            return wrapDecimalOperand(sumValue);
        }
        patchModelEventInfo(this.eventArgs, x => {
            x.processedBySum = true;
        });
        return (
            wrapDecimalOperand(sumValue)
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                .minus(wrapDecimalOperand(this.eventArgs.oldValue))
                // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                .plus(wrapDecimalOperand(this.eventArgs.value))
        );
    }
}
